/**
 * Keeps the secrets that links carry in their URLs out of PostHog.
 *
 * Links this product sends carry secrets in the URL: the email-verification
 * link (`/auth?signup_secret=…&email_address=…`), the password-reset link
 * (`/reset?reset_password_secret=…`), the post-signup redirect
 * (`/auth?email_address=…`) and an administrator's support hand-off
 * (`/support-session#token=…`). PostHog records URLs on every event
 * (`$current_url`, `$referrer`, `$session_entry_url`, the `$initial_*` person
 * properties, autocaptured `href`s, heatmap keys) and sends the initial URL
 * and referrer with every `/flags` request, so without this those secrets and
 * the email address would sit in PostHog for anyone with project access.
 *
 * Two hooks, both installed by analytics.ts:
 *  - `maskEventSecrets` (inside `before_send`) masks every captured event.
 *  - `maskStoredInitialPersonInfo` (`loaded`) masks the initial URL and
 *    referrer PostHog persists and sends to `/flags`, which never passes
 *    through `before_send`.
 *
 * Same parameters, placeholder and guarantees as apps/web
 * (src/analytics/urlSecrets.ts), so both products record the same masked
 * URLs. Pure apart from the `document`/`window` defaults of the seed.
 */
import type { CaptureResult, PostHog } from 'posthog-js';

/** Parameters whose values must never reach PostHog in a recorded URL. */
export const MASKED_URL_PARAMS = ['auth_code', 'signup_secret', 'reset_password_secret', 'token', 'email_address'] as const;
export const MASKED = '<masked>';

const NAMES = MASKED_URL_PARAMS.join('|');
// `name=value` after a query, fragment or `;` delimiter; the value runs to the
// next delimiter. The name is kept so an analyst can still see which link was
// opened.
const PLAIN = new RegExp(`([?&#;](?:${NAMES})=)[^&#;]*`, 'gi');
// The same URL nested, percent-encoded, inside another URL's parameter (a
// `?redirect=` or a third-party referrer). Its value ends at an encoded `&`,
// `#` or `;`, or at a real delimiter of the outer URL.
const ENCODED = new RegExp(`((?:%3F|%26|%23|%3B)(?:${NAMES})%3D)(?:(?!%26|%23|%3B)[^&#;])*`, 'gi');

/** Replace the value of every sensitive parameter in a URL-like string. */
export const maskUrlSecrets = (value: string): string => value.replace(PLAIN, `$1${MASKED}`).replace(ENCODED, `$1${MASKED}`);

type Container = Record<string, unknown> | unknown[];

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value) as unknown;
  return prototype === Object.prototype || prototype === null;
};

// Deep, in place: URLs also arrive nested (`properties.$set`, autocaptured
// `$elements[].attr__href`) and as object keys (`$$heatmap` data is keyed by
// page URL). PostHog has already copied the event it hands to `before_send`,
// so mutating it touches nothing the library keeps.
const maskInPlace = (container: Container): void => {
  if (Array.isArray(container)) {
    container.forEach((value, index) => {
      if (typeof value === 'string') container[index] = maskUrlSecrets(value);
      else if (Array.isArray(value) || isPlainObject(value)) maskInPlace(value);
    });
    return;
  }
  for (const [key, value] of Object.entries(container)) {
    if (Array.isArray(value) || isPlainObject(value)) maskInPlace(value);
    const maskedKey = maskUrlSecrets(key);
    const maskedValue = typeof value === 'string' ? maskUrlSecrets(value) : value;
    if (maskedKey !== key) {
      Reflect.deleteProperty(container, key);
      container[maskedKey] = maskedValue;
    } else if (maskedValue !== value) {
      container[key] = maskedValue;
    }
  }
};

/** Mask URL secrets everywhere in an outgoing event: its properties, `$set` and `$set_once`. */
export const maskEventSecrets = (event: CaptureResult): CaptureResult => {
  for (const part of [event.properties, event.$set, event.$set_once]) {
    if (isPlainObject(part)) maskInPlace(part);
  }
  return event;
};

// posthog-js's persistence keys for the first URL and referrer it saw. Not
// public API: `$initial_person_info` (`{ r: referrer, u: url }`) becomes the
// `$initial_*` person properties and rides along on every `/flags` request;
// `$initial_referrer_info` is the record older releases wrote, which the
// current one still reads (a cookie on `.lemon-ai.com` is shared with apps/web
// and may predate its masking). The version is pinned in package.json, and
// urlSecrets.test.ts plus tests/e2e/analytics-url-secrets.spec.ts fail if an
// upgrade changes either.
export const INITIAL_PERSON_INFO = '$initial_person_info';
export const LEGACY_INITIAL_REFERRER_INFO = '$initial_referrer_info';
const DIRECT = '$direct';
const URL_LIMIT = 1000;

/** Where the visitor arrived from and at, as posthog-js records it (before masking). */
export type ArrivalUrls = { referrer: string; url: string };

const currentArrival = (): ArrivalUrls => ({ referrer: document.referrer, url: window.location.href });

const maskedCopy = (value: unknown): unknown => {
  if (typeof value === 'string') return maskUrlSecrets(value);
  if (!Array.isArray(value) && !isPlainObject(value)) return value;
  const copy = structuredClone(value);
  maskInPlace(copy);
  return copy;
};

/**
 * Store the initial URL and referrer masked before PostHog records them, and
 * re-mask any copy an earlier visit already stored.
 *
 * PostHog writes `$initial_person_info` once, on the first capture, with
 * `register_once`, and never overwrites it; seeding it here, at load, is what
 * makes its own write a no-op.
 */
export const maskStoredInitialPersonInfo = (
  posthog: Pick<PostHog, 'get_property' | 'register'>,
  arrival: ArrivalUrls = currentArrival(),
): void => {
  const stored: unknown = posthog.get_property(INITIAL_PERSON_INFO);
  const seeded = stored === undefined || stored === null;
  const records: [string, unknown][] = [
    [INITIAL_PERSON_INFO, seeded ? { r: (arrival.referrer || DIRECT).substring(0, URL_LIMIT), u: arrival.url.substring(0, URL_LIMIT) } : stored],
    [LEGACY_INITIAL_REFERRER_INFO, posthog.get_property(LEGACY_INITIAL_REFERRER_INFO)],
  ];
  const updates: Record<string, unknown> = {};
  for (const [key, value] of records) {
    if (value === undefined || value === null) continue;
    const masked = maskedCopy(value);
    if ((key === INITIAL_PERSON_INFO && seeded) || JSON.stringify(masked) !== JSON.stringify(value)) updates[key] = masked;
  }
  // `register` writes the same persistence record PostHog reads these from.
  if (Object.keys(updates).length > 0) posthog.register(updates);
};
